"use client";

import { useCallback, useEffect, useState, type ComponentType, type SVGProps } from "react";
import cnBind from "classnames/bind";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { IcAnalytics, IcHome, IcManageAccounts } from "@/shared/assests/svg";
import {
    AdminNavIconBriefcase,
    AdminNavIconInbox,
    AdminNavIconMegaphone,
    AdminNavIconMessage,
    AdminNavIconUsers,
} from "@/shared/constants/adminNavIcons";
import { ADMIN_PATHS } from "@/shared/constants/adminPaths";
import { useAuth } from "@/shared/auth/AuthContext";
import { AdminNotificationsBell } from "@/components/AdminNotificationsBell/AdminNotificationsBell";
import { dispatchAdminDataRefresh } from "@/shared/admin/adminRefresh";

import styles from "./AdminSideNav.module.scss";

const cx = cnBind.bind(styles);

const COLLAPSE_KEY = "tezam_admin_nav_collapsed";

type NavItem = { href: string; label: string; Icon: ComponentType<SVGProps<SVGSVGElement>> };

type NavSection = { id: string; title: string; items: NavItem[] };

const SECTIONS: NavSection[] = [
    {
        id: "analytics",
        title: "Аналитика",
        items: [{ href: "/admin", label: "Дашборд", Icon: IcAnalytics }],
    },
    {
        id: "content",
        title: "Контент",
        items: [
            { href: "/admin/articles", label: "Статьи", Icon: IcManageAccounts },
            { href: "/admin/news", label: "Новости", Icon: IcManageAccounts },
            { href: "/admin/portfolio", label: "Портфолио", Icon: IcManageAccounts },
            { href: "/admin/ai", label: "Генерация AI", Icon: IcManageAccounts },
            { href: "/admin/sections", label: "Разделы сайта", Icon: IcHome },
        ],
    },
    {
        id: "crm",
        title: "CRM",
        items: [
            { href: ADMIN_PATHS.applications, label: "Входящие заявки", Icon: AdminNavIconInbox },
            { href: ADMIN_PATHS.catalogSubmissions, label: "Каталог клиентов", Icon: AdminNavIconInbox },
            { href: ADMIN_PATHS.partners, label: "Партнёры", Icon: AdminNavIconUsers },
            { href: ADMIN_PATHS.chat, label: "Чаты на сайте", Icon: AdminNavIconMessage },
        ],
    },
    {
        id: "users",
        title: "Пользователи",
        items: [
            { href: "/admin/clients", label: "Клиенты", Icon: AdminNavIconUsers },
            { href: "/admin/staff", label: "Сотрудники", Icon: AdminNavIconBriefcase },
        ],
    },
    {
        id: "notifications",
        title: "Уведомления",
        items: [
            { href: ADMIN_PATHS.notifications, label: "Мои уведомления", Icon: AdminNavIconInbox },
            { href: ADMIN_PATHS.notificationsBroadcast, label: "Рассылка", Icon: AdminNavIconMegaphone },
        ],
    },
];

function linkActive(href: string, pathname: string): boolean {
    if (href === "/admin") return pathname === "/admin" || pathname === "/admin/";
    const norm = (pathname.replace(/\/$/, "") || "/") as string;
    const hrefNorm = (href.replace(/\/$/, "") || "/") as string;
    if (hrefNorm === ADMIN_PATHS.notifications) {
        return norm === hrefNorm;
    }
    return norm === hrefNorm || norm.startsWith(`${hrefNorm}/`);
}

function sectionContainsActive(section: NavSection, pathname: string): boolean {
    return section.items.some((item) => linkActive(item.href, pathname));
}

function initialsFromUser(email: string, fullName?: string | null): string {
    if (fullName?.trim()) {
        const parts = fullName.trim().split(/\s+/).slice(0, 2);
        return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || email.slice(0, 2).toUpperCase();
    }
    return email.slice(0, 2).toUpperCase();
}

export function AdminSideNav() {
    const pathname = usePathname() ?? "";
    const { user, logout } = useAuth();
    const queryClient = useQueryClient();
    const fetching = useIsFetching();
    const [collapsed, setCollapsed] = useState(false);
    const [openSectionId, setOpenSectionId] = useState<string | null>(null);

    const navSections = SECTIONS;

    useEffect(() => {
        try {
            const v = localStorage.getItem(COLLAPSE_KEY);
            if (v === "1") setCollapsed(true);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        const match = navSections.find((s) => sectionContainsActive(s, pathname));
        if (match) setOpenSectionId(match.id);
    }, [pathname]);

    const toggleCollapsed = useCallback(() => {
        setCollapsed((c) => {
            const next = !c;
            try {
                localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    const onSectionHeaderClick = useCallback((id: string) => {
        setOpenSectionId((cur) => (cur === id ? null : id));
    }, []);

    return (
        <aside className={cx("shell", "admin-shell-nav", { shellCollapsed: collapsed, collapsed })}>
            <div className={cx("brand")}>
                <Link href="/admin" className={cx("brandLink")} aria-label="Tezam Admin — дашборд">
                    <span className={cx("brandMark")}>T</span>
                    <div className={cx("brandText")}>
                        <span className={cx("brandTitle")}>Tezam</span>
                        <span className={cx("brandSub")}>Панель управления</span>
                    </div>
                </Link>
                <button
                    type="button"
                    className={cx("collapseBtn")}
                    onClick={toggleCollapsed}
                    title={collapsed ? "Развернуть меню" : "Свернуть меню"}
                    aria-expanded={!collapsed}
                >
                    {collapsed ? "›" : "‹"}
                </button>
            </div>

            <nav className={cx("navScroll")} aria-label="Разделы админки">
                {collapsed ? (
                    <ul className={cx("flatList")}>
                        {navSections.flatMap((section) =>
                            section.items.map((item) => {
                                const Icon = item.Icon;
                                const active = linkActive(item.href, pathname);
                                return (
                                    <li key={`${section.id}-${item.href}`}>
                                        <Link
                                            href={item.href}
                                            className={cx("link", { linkActive: active })}
                                            title={item.label}
                                        >
                                            <Icon className={cx("icon")} aria-hidden />
                                        </Link>
                                    </li>
                                );
                            }),
                        )}
                    </ul>
                ) : (
                    navSections.map((section) => {
                        const open = openSectionId === section.id;
                        return (
                            <div className={cx("accordionSection", { accordionSectionOpen: open })} key={section.id}>
                                <button
                                    type="button"
                                    className={cx("sectionHeader")}
                                    onClick={() => onSectionHeaderClick(section.id)}
                                    aria-expanded={open}
                                >
                                    <span className={cx("sectionTitle")}>{section.title}</span>
                                    <span className={cx("sectionChevron")} aria-hidden>
                                        ›
                                    </span>
                                </button>
                                <div className={cx("accordionPanel", { accordionPanelOpen: open })}>
                                    <div className={cx("accordionPanelInner")} aria-hidden={!open}>
                                        <ul className={cx("sectionList")}>
                                            {section.items.map((item) => {
                                                const Icon = item.Icon;
                                                const active = linkActive(item.href, pathname);
                                                return (
                                                    <li key={item.href}>
                                                        <Link
                                                            href={item.href}
                                                            className={cx("link", {
                                                                linkActive: active,
                                                                linkNested: item.href !== "/admin",
                                                            })}
                                                        >
                                                            <Icon className={cx("icon")} aria-hidden />
                                                            <span>{item.label}</span>
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </nav>

            {user ? (
                <div className={cx("profileBar")}>
                    <div className={cx("sidebarBellSlot")}>
                        <AdminNotificationsBell variant="sidebar" sidebarCollapsed={collapsed} />
                    </div>
                    <div className={cx("profileMain")}>
                        <span className={cx("profileAvatar")} aria-hidden>
                            {initialsFromUser(user.email, user.fullName)}
                        </span>
                        <div className={cx("profileText")}>
                            <span className={cx("profileName")}>{user.fullName?.trim() || "Аккаунт"}</span>
                            <span className={cx("profileEmail")}>{user.email}</span>
                        </div>
                    </div>
                    <div className={cx("profileActions")}>
                        <span className={cx("sessionPill")}>
                            <span className={cx("presence")} title="Сессия активна" aria-hidden />
                            В системе
                        </span>
                        <div className={cx("profileBtnRow")}>
                            <button
                                type="button"
                                className={cx("refreshBtn")}
                                disabled={fetching > 0}
                                onClick={() => {
                                    dispatchAdminDataRefresh();
                                    void queryClient.invalidateQueries();
                                }}
                            >
                                {fetching > 0 ? "Обновление…" : "Обновить данные"}
                            </button>
                            <button type="button" className={cx("logoutBtn")} onClick={() => void logout()}>
                                Выйти
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </aside>
    );
}
